import { FormProvider } from "@arp/react-ndbx";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Application, ccRetrieveContent } from "../../../config/const/enums";
import { queryKeys } from "../../../hooks/dataHooks/queryKeys";
import useAuth from "../../../hooks/useAuth";
import useSharedData from "../../../hooks/useSharedData";
import { PATH_CONTACT_CENTER } from "../../../routes/paths";
import { retrieveSelectedRow } from "../../../schema/retrieveQuoteSchema";
import {
  getDateDDMMYYYFormat,
  getExpiredPolicyStatus,
} from "../../../utilities/preQualificationUtils";

function RetrieveResultsTable({
  searchQuoteDetails,
  cancelSearchResults,
  resetChildForm,
}) {
  const queryClient = useQueryClient();
  const { getOktaAccessToken } = useAuth();
  const navigate = useNavigate();
  const { handleEventChange } = useSharedData();

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: "8px",
    paddingBottom: "8px",
  }));

  const MediumTableCell = styled(TableCell)(({ theme }) => ({
    width: "15%",
    paddingTop: "8px",
    paddingBottom: "8px",
  }));

  const methods = useForm({
    resolver: yupResolver(retrieveSelectedRow),
    mode: "all",
    reValidateMode: "all",
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isValid },
    reset,
    resetField,
    setValue,
  } = methods;

  useEffect(() => {
    reset();
    resetField("selectedRow");
    setValue("selectedRow", "");
  }, [resetChildForm]);

  const handleValidationAndSubmit = async () => {
    try {
      methods.trigger().then(async (isValid) => {
        if (isValid) {
          const formData = getValues();
          let retrieveOktaAccessToken = await getOktaAccessToken();
          const { accessToken } = { ...retrieveOktaAccessToken };
          const payload = {
            oktaAccessToken: accessToken,
            transactionUid: formData.selectedRow,
            expired: getExpiredPolicyStatus(
              formData.selectedRow,
              searchQuoteDetails
            ),
            application: Application.CONTACT_CENTER,
          };
          /*
          need to set the payload in tanstack and call retrieve quote API in the RetrieveLoading page.
          In the RetrieveLoading loading page we will determine the status and redirect the user
          */
          sessionStorage.setItem("retrieveQuoteJourney", true);
          handleEventChange({
            name: "retrieveQuoteJourney",
            value: true,
          });
          queryClient.setQueryData(
            [queryKeys.RETRIEVE_QUOTE_TOKEN_DETAIL],
            payload || {}
          );
          navigate(PATH_CONTACT_CENTER.retrieveQuoteLoading);
        }
      });
    } catch (error) {
      console.error("Error while submitting the form - ", error);
    }
  };

  return (
    <FormProvider methods={methods}>
      <TableContainer sx={{ marginBottom: { xs: 8 } }}>
        <Table>
          <TableHead>
            <TableRow>
              <MediumTableCell>{ccRetrieveContent.REF_NO}</MediumTableCell>
              <MediumTableCell>
                {ccRetrieveContent.INSURED_NAME}
              </MediumTableCell>
              <MediumTableCell>{ccRetrieveContent.DOB}</MediumTableCell>
              <MediumTableCell>{ccRetrieveContent.START_DATE}</MediumTableCell>
              <StyledTableCell>{ccRetrieveContent.SOURCE}</StyledTableCell>
              <StyledTableCell>
                {ccRetrieveContent.INTERMEDIARY}
              </StyledTableCell>
              <StyledTableCell>{ccRetrieveContent.ACCOUNT}</StyledTableCell>
              <StyledTableCell>{ccRetrieveContent.DECLINED}</StyledTableCell>
              <StyledTableCell>{ccRetrieveContent.EXPIRED}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchQuoteDetails.map((quoteDetail, index) => (
              <TableRow
                key={index}
                data-identifier={"quote-detail-row-" + index}
              >
                <StyledTableCell>
                  <FormControl>
                    <Controller
                      name="selectedRow"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <FormControlLabel
                            label={
                              <Typography variant={"p2-bold"}>
                                {quoteDetail.quoteNumber}
                              </Typography>
                            }
                            value={quoteDetail.transactionUid}
                            control={<Radio />}
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </StyledTableCell>
                <StyledTableCell align="left">
                  {quoteDetail.insuredName}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {getDateDDMMYYYFormat(quoteDetail?.dateOfBirth)}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {getDateDDMMYYYFormat(quoteDetail?.startDate)}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {quoteDetail.channel === "LIFEINT" ? "DIRECT" : "CC"}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {ccRetrieveContent.ALLIANZ}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {ccRetrieveContent.ALLIANZ}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {" "}
                  {quoteDetail.processStatus === ("D" || "R") ? "Y" : "N"}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {quoteDetail.expired ? "Y" : "N"}
                </StyledTableCell>
              </TableRow>
            ))}
            {searchQuoteDetails && searchQuoteDetails.length === 0 && (
              <>
                <TableRow>
                  <TableCell align="center" colSpan={9}>
                    {ccRetrieveContent.NO_DATA_AVAILABLE}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 10 }}
      >
        <Button
          variant="contained"
          color="secondary"
          sx={{ width: "180px" }}
          onClick={cancelSearchResults}
        >
          {ccRetrieveContent.CANCEL}
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: "180px" }}
          disabled={!isValid}
          onClick={handleValidationAndSubmit}
        >
          {ccRetrieveContent.CONTINUE}
        </Button>
      </Stack>
    </FormProvider>
  );
}

export default RetrieveResultsTable;
